import { useCallback } from 'react';
import { useCurrentLocale } from '@locales/client';
import { processText } from '@/shared/libs/process-text';
import { useUser } from '@/shared/providers/user-provider/use-user';

export const useGameAccessCheck = () => {
  const { user, notifications } = useUser();
  const locale = useCurrentLocale();

  const hasBonus = useCallback(
    (game) => {
      const { id } = game || {};
      const { bonusInfo } = notifications || {};
      const { games, provider } = bonusInfo || {};

      return (
        games?.includes(id) ||
        (!games && !provider && notifications?.bonus?.isActive)
      );
    },
    [notifications]
  );

  const isDisabled = useCallback(
    (game) => {
      const { id, denyBonus } = game || {};
      const isActive = notifications?.bonus?.isActive;
      const hasGames = notifications?.bonusInfo?.games?.length > 0;
      const currentHasBonus = hasBonus(game);

      if (!user?.id) return false;

      if (isActive && hasGames) {
        return !currentHasBonus || denyBonus;
      }

      return isActive && denyBonus;
    },
    [user, notifications, hasBonus]
  );

  const canOpenGame = useCallback(
    (game) => {
      if (!game) return false;
      const { denyBonus } = game;

      if (!user?.isAuth) return true;
      if (denyBonus && notifications?.bonus?.isActive) return false;

      return !isDisabled(game);
    },
    [user, notifications, isDisabled]
  );

  const getGamePath = useCallback(
    (game, isDemo = false) => {
      if (!game) return '';
      const { providerId, title } = game;
      return `/${locale}/games/${processText(providerId)}/${processText(title)}/${isDemo ? 'demo' : 'play'}`;
    },
    [user?.isAuth, locale]
  );
  const getGameInfoPath = useCallback(
    (game) => {
      if (!game) return '';
      const { providerId, title } = game;
      return `/${locale}/games/${processText(providerId)}/${processText(title)}`;
    },
    [locale]
  );

  return {
    canOpenGame,
    isDisabled,
    hasBonus,
    getGamePath,
    getGameInfoPath,
  };
};
